import { memo, useCallback, useState } from 'react'

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { RowActions, useColumns, TeamCreationDialogContent } from 'domains/Organizations/Organization/TeamManagement'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { teamsOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Teams'
import { membersOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Teams/Team'
import { teamQueryKeys } from 'services/queryKeys'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const { organizationId } = teamsOverviewRoute.useParams()
  const navigate = useNavigate({ from: teamsOverviewRoute.fullPath })
  const { query } = teamsOverviewRoute.useSearch()

  const [dialogOpen, setDialogOpen] = useState(false)

  const teamsQuery = useInfiniteQuery(teamQueryKeys.list({ organizationId, query }))
  const userMembers = teamsQuery.data?.pages.flatMap((page) => page.items)

  const columns = useColumns()

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:My.Organization.searchTeams')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () =>
              queryClient.invalidateQueries({
                queryKey: teamQueryKeys.list({ organizationId }).queryKey,
              }),
            [organizationId, queryClient],
          )}
          createAction={() => setDialogOpen(true)}
        />
      </ActionStrip>

      <Table
        data={userMembers ?? []}
        columns={columns}
        isInitialLoadingGlobal={teamsQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={teamsOverviewRoute.fullPath}
            to={membersOverviewRoute.to}
            params={{ teamId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id']}
        ActionMenuProps={(row) => ({
          title: row.original.name || '',
          entity: t('entity:org.team', 'Team', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <TeamCreationDialogContent />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
